import type { ComponentType, HTMLAttributes, ReactNode } from 'react'
import { CheckCircle, Info, Rocket, Warning, WarningCircle, X, type IconProps } from './icons'

export type AlertTone = 'default' | 'info' | 'success' | 'warning' | 'danger' | 'brand'

const TONE_ICON: Record<AlertTone, ComponentType<IconProps>> = {
  default: Info,
  info: Info,
  success: CheckCircle,
  warning: Warning,
  danger: WarningCircle,
  brand: Rocket,
}

export type AlertProps = Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'role'> & {
  /** `brand` is for upgrade prompts only. */
  tone?: AlertTone
  /** Title Case label, 14px medium weight. */
  title?: ReactNode
  /** Sentence case prose, 13px secondary gray. Takes the title style when there is no title. */
  description?: ReactNode
  /** Replaces the default tone icon. */
  icon?: ComponentType<IconProps>
  /** One regular button. It sits on the right, or under the text when the alert is narrow. */
  action?: ReactNode
  /** Shows a dismiss icon button. Leave it out for alerts that must stay. */
  /** Adds a close button. Ignored on the brand tone: upgrade prompts cannot be dismissed. */
  onDismiss?: () => void
  dismissLabel?: string
}

/**
 * The one notice surface for persistent state: gating, degraded features,
 * form-level errors and upgrade prompts. Action feedback is a Toast instead.
 */
export function Alert({ tone = 'default', title, description, icon, action, onDismiss, dismissLabel = 'Dismiss', className = '', ...props }: AlertProps) {
  const Icon = icon ?? TONE_ICON[tone]
  const hasTitle = title !== undefined && title !== null && title !== false
  const hasDescription = description !== undefined && description !== null && description !== false
  const layout = hasTitle && hasDescription ? 'ui-alert--stacked' : 'ui-alert--single'

  return (
    <div
      {...props}
      role={tone === 'danger' ? 'alert' : 'status'}
      className={['ui-alert', `ui-alert--${tone}`, layout, hasDescription && 'ui-alert--has-description', action ? 'ui-alert--has-action' : '', className].filter(Boolean).join(' ')}
    >
      <div className="ui-alert__grid">
        <span className="ui-alert__tile" aria-hidden="true"><Icon size={20} /></span>
        <div className="ui-alert__text">
          {hasTitle && <p className="ui-alert__title">{title}</p>}
          {hasDescription && <div className={hasTitle ? 'ui-alert__description' : 'ui-alert__title'}>{description}</div>}
        </div>
        {action && <div className="ui-alert__action">{action}</div>}
        {onDismiss && tone !== 'brand' && (
          <button type="button" className="ui-icon-button ui-alert__dismiss" aria-label={dismissLabel} onClick={onDismiss}>
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}
